import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/solid';
import { DeviceTabletIcon } from '@heroicons/react/24/outline';
import { CurrencyToggle } from './CurrencyToggle';

type Currency = 'NZD' | 'USD';
type Region = 'Coastal only' | 'Multiple regions' | 'Worldwide';
type PlanKey = 'coastal edition' | 'bluewater' | 'expedition';

const NZD_USD_RATE = 0.60;

interface PlanTier {
  key: PlanKey;
  name: string;
  quarterlyPriceNZD: number;
  tokensPerQuarter: number;
  maxZoom: number;
}

const PLAN_TIERS: PlanTier[] = [
  { key: 'coastal edition',    name: 'Coastal',    quarterlyPriceNZD: 78,  tokensPerQuarter: 90_000,  maxZoom: 15 },
  { key: 'bluewater',  name: 'Blue Water', quarterlyPriceNZD: 183, tokensPerQuarter: 300_000, maxZoom: 17 },
  { key: 'expedition', name: 'Expedition', quarterlyPriceNZD: 519, tokensPerQuarter: 900_000, maxZoom: 20 },
];

interface ZoomCluster {
  value: number;
  label: string;
  range: string;
  tileMultiplier: number;
}

const zoomClusters: ZoomCluster[] = [
  { value: 3,  label: 'Global',   range: 'z0–5',   tileMultiplier: 1 },
  { value: 8,  label: 'Regional', range: 'z5–10',  tileMultiplier: 4 },
  { value: 13, label: 'Coastal',  range: 'z10–15', tileMultiplier: 16 },
  { value: 18, label: 'Detail',   range: 'z15–20', tileMultiplier: 64 },
];

interface RegionPreset {
  tiles: number;
  zoom: number;
  assumption: string;
}

const regionPresets: Record<Region, RegionPreset> = {
  'Coastal only': {
    tiles: 200,
    zoom: 13,
    assumption:
      'Assumes ~3 coastal trips/month, coastal-level zoom for harbour approaches and anchorages.',
  },
  'Multiple regions': {
    tiles: 600,
    zoom: 8,
    assumption:
      'Assumes weekly navigation across 2–3 regions, regional zoom for passage planning.',
  },
  Worldwide: {
    tiles: 1500,
    zoom: 3,
    assumption:
      'Assumes regular ocean passages with broad global coverage, plus detail tiles for port approaches.',
  },
};

function getClusterIndex(zoomValue: number): number {
  return zoomClusters.findIndex((c) => c.value === zoomValue);
}

function getClusterLabel(zoomValue: number): string {
  const cluster = zoomClusters.find((c) => c.value === zoomValue);
  return cluster ? cluster.label : 'Regional';
}

function getCluster(zoomValue: number): ZoomCluster {
  return zoomClusters.find((c) => c.value === zoomValue) ?? zoomClusters[1];
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString();
}

function formatPrice(nzd: number, currency: Currency): string {
  const amount = currency === 'USD' ? nzd * NZD_USD_RATE : nzd;
  return `$${Math.round(amount)}`;
}

/* ─── Integrated Zoom Pyramid ─── */
function ZoomPyramid({
  activeClusterIndex,
  onChange,
}: {
  activeClusterIndex: number;
  onChange: (zoomValue: number) => void;
}) {
  const layers = [
    { gridSize: 1, label: 'Global' },
    { gridSize: 2, label: 'Regional' },
    { gridSize: 3, label: 'Coastal' },
    { gridSize: 5, label: 'Detail' },
  ];

  const svgWidth = 310;
  const svgHeight = 240;
  const tileW = 11;
  const tileH = 6;
  const layerGap = 52;
  const startY = 24;
  const centerX = 190;
  const scaleX = centerX - 5 * tileW - 28;

  function getLayerCorners(cx: number, cy: number, gridSize: number) {
    const hw = gridSize * tileW;
    const hh = gridSize * tileH;
    return {
      top: { x: cx, y: cy - hh },
      right: { x: cx + hw, y: cy },
      bottom: { x: cx, y: cy + hh },
      left: { x: cx - hw, y: cy },
    };
  }

  function drawIsometricGrid(
    cx: number,
    cy: number,
    gridSize: number,
    layerIndex: number,
  ) {
    const tiles: JSX.Element[] = [];
    const isActive = layerIndex === activeClusterIndex;
    const isPast = layerIndex < activeClusterIndex;

    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const isoX = (col - row) * tileW;
        const isoY = (col + row) * tileH;
        const offsetY = -(gridSize - 1) * tileH;
        const tx = cx + isoX;
        const ty = cy + isoY + offsetY;
        const points = [
          `${tx},${ty - tileH}`,
          `${tx + tileW},${ty}`,
          `${tx},${ty + tileH}`,
          `${tx - tileW},${ty}`,
        ].join(' ');

        tiles.push(
          <motion.polygon
            key={`${layerIndex}-${row}-${col}`}
            points={points}
            animate={{
              fill: isActive
                ? 'rgba(34, 211, 238, 0.15)'
                : isPast
                  ? 'rgba(34, 211, 238, 0.05)'
                  : 'rgba(30, 41, 59, 0.5)',
              stroke: isActive
                ? '#22d3ee'
                : isPast
                  ? 'rgba(34, 211, 238, 0.3)'
                  : '#334155',
              strokeWidth: isActive ? 1.2 : 0.7,
            }}
            transition={{ duration: 0.35 }}
          />,
        );
      }
    }
    return tiles;
  }

  const layerCenters = layers.map((_, i) => startY + i * layerGap);

  const topCorners = getLayerCorners(centerX, layerCenters[0], layers[0].gridSize);
  const bottomCorners = getLayerCorners(centerX, layerCenters[3], layers[3].gridSize);

  const dotPositions = layerCenters.map(y => ({ x: scaleX, y }));

  return (
    <div
      className="select-none"
      role="slider"
      aria-label="Zoom level cluster"
      aria-valuemin={0}
      aria-valuemax={3}
      aria-valuenow={activeClusterIndex}
      aria-valuetext={zoomClusters[activeClusterIndex]?.label}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp' && activeClusterIndex > 0) {
          onChange(zoomClusters[activeClusterIndex - 1].value);
        } else if (
          e.key === 'ArrowDown' &&
          activeClusterIndex < zoomClusters.length - 1
        ) {
          onChange(zoomClusters[activeClusterIndex + 1].value);
        }
      }}
    >
      <svg
        width="100%"
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="max-w-full"
      >
        {/* Pyramid dashed outline */}
        <line
          x1={topCorners.left.x} y1={topCorners.left.y}
          x2={bottomCorners.left.x} y2={bottomCorners.left.y}
          stroke="#64748b" strokeWidth={1} strokeDasharray="5 3"
        />
        <line
          x1={topCorners.right.x} y1={topCorners.right.y}
          x2={bottomCorners.right.x} y2={bottomCorners.right.y}
          stroke="#64748b" strokeWidth={1} strokeDasharray="5 3"
        />
        <line
          x1={topCorners.top.x} y1={topCorners.top.y}
          x2={bottomCorners.top.x} y2={bottomCorners.top.y}
          stroke="#64748b" strokeWidth={1} strokeDasharray="5 3"
        />
        <line
          x1={topCorners.bottom.x} y1={topCorners.bottom.y}
          x2={bottomCorners.bottom.x} y2={bottomCorners.bottom.y}
          stroke="#64748b" strokeWidth={1} strokeDasharray="5 3"
        />

        {/* Isometric tile grids */}
        {layers.map((layer, i) => (
          <g key={i}>
            {drawIsometricGrid(centerX, layerCenters[i], layer.gridSize, i)}
          </g>
        ))}

        {/* Right-side layer labels */}
        {layers.map((layer, i) => {
          const isActive = i === activeClusterIndex;
          const corners = getLayerCorners(centerX, layerCenters[i], layer.gridSize);
          return (
            <text
              key={`label-${i}`}
              x={corners.right.x + 10}
              y={corners.right.y + 3}
              className="text-[13px]"
              fill={isActive ? '#22d3ee' : '#64748b'}
              fontWeight={isActive ? 600 : 400}
              fontFamily="Inter, sans-serif"
            >
              {layer.label}
            </text>
          );
        })}

        {/* Vertical scale line (background) */}
        <line
          x1={scaleX} y1={dotPositions[0].y}
          x2={scaleX} y2={dotPositions[3].y}
          stroke="#334155" strokeWidth={2} strokeLinecap="round"
        />

        {/* Vertical scale line (active portion) */}
        {activeClusterIndex > 0 && (
          <motion.line
            x1={scaleX}
            y1={dotPositions[0].y}
            animate={{ y2: dotPositions[activeClusterIndex].y }}
            x2={scaleX}
            y2={dotPositions[0].y}
            stroke="#22d3ee"
            strokeWidth={2}
            strokeLinecap="round"
            transition={{ duration: 0.3, ease: 'easeOut' }}
          />
        )}


        {/* Interactive dots along left edge */}
        {dotPositions.map((dot, i) => {
          const isActive = i === activeClusterIndex;
          const isPast = i < activeClusterIndex;
          return (
            <g
              key={`dot-${i}`}
              className="cursor-pointer"
              onClick={() => onChange(zoomClusters[i].value)}
            >
              {/* Larger invisible hit area */}
              <circle cx={dot.x} cy={dot.y} r={16} fill="transparent" />
              <motion.circle
                cx={dot.x}
                cy={dot.y}
                animate={{ r: isActive ? 7 : 5 }}
                fill={
                  isActive
                    ? '#22d3ee'
                    : isPast
                      ? 'rgba(34, 211, 238, 0.4)'
                      : '#334155'
                }
                transition={{ duration: 0.2 }}
              />
              {/* Label */}
              <text
                x={dot.x - 14}
                y={dot.y + 1}
                textAnchor="end"
                fontSize={13}
                fontWeight={isActive ? 700 : 500}
                fill={isActive ? '#22d3ee' : '#94a3b8'}
                fontFamily="Inter, sans-serif"
                dominantBaseline="middle"
              >
                {zoomClusters[i].label}
              </text>
              {/* Range sub-label */}
              <text
                x={dot.x - 14}
                y={dot.y + 17}
                textAnchor="end"
                fontSize={13}
                fill="#64748b"
                fontFamily="Inter, sans-serif"
              >
                {zoomClusters[i].range}
              </text>
            </g>
          );
        })}

        {/* Coarse / Detailed annotations */}
        <text
          x={scaleX}
          y={dotPositions[0].y - 14}
          textAnchor="middle"
          fontSize={13}
          fill="#64748b"
          fontFamily="Inter, sans-serif"
          letterSpacing="0.05em"
        >
          COARSE
        </text>
        <text
          x={scaleX}
          y={dotPositions[3].y + 36}
          textAnchor="middle"
          fontSize={13}
          fill="#64748b"
          fontFamily="Inter, sans-serif"
          letterSpacing="0.05em"
        >
          DETAILED
        </text>
      </svg>
    </div>
  );
}

/* ─── Main Calculator ─── */
interface TokenCalculatorProps {
  onSelect: (slug: string) => void;
  onSwitchView?: () => void;
}

function estimateQuarterlyTokens(
  monthlyTiles: number,
  zoomCluster: ZoomCluster,
): number {
  const baseTiles = monthlyTiles * zoomCluster.tileMultiplier;
  return Math.round(baseTiles * 3);
}

function recommendPlan(quarterlyTokens: number, zoomValue: number): PlanTier {
  for (const tier of PLAN_TIERS) {
    if (quarterlyTokens <= tier.tokensPerQuarter && zoomValue <= tier.maxZoom) {
      return tier;
    }
  }
  return PLAN_TIERS[PLAN_TIERS.length - 1];
}

export function TokenCalculator({ onSelect, onSwitchView }: TokenCalculatorProps) {
  const [currency, setCurrency] = useState<Currency>('NZD');
  const [tiles, setTiles] = useState<number>(200);
  const [zoom, setZoom] = useState<number>(13);
  const [region, setRegion] = useState<Region>('Coastal only');

  const handleRegionChange = (newRegion: Region) => {
    setRegion(newRegion);
    const preset = regionPresets[newRegion];
    setTiles(preset.tiles);
    setZoom(preset.zoom);
  };

  const cluster = getCluster(zoom);
  const activeClusterIndex = getClusterIndex(zoom);
  const clusterLabel = getClusterLabel(zoom);

  const quarterlyTokens = useMemo(
    () => estimateQuarterlyTokens(tiles, cluster),
    [tiles, cluster],
  );

  const recommended = useMemo(
    () => recommendPlan(quarterlyTokens, zoom),
    [quarterlyTokens, zoom],
  );

  const planFit = useMemo(() => {
    const pct = recommended.tokensPerQuarter > 0
      ? Math.round((quarterlyTokens / recommended.tokensPerQuarter) * 100)
      : 0;
    const headroom = recommended.tokensPerQuarter - quarterlyTokens;
    return { pct, headroom };
  }, [quarterlyTokens, recommended]);

  const nextPlanDown = useMemo(() => {
    const idx = PLAN_TIERS.findIndex((t) => t.key === recommended.key);
    return idx > 0 ? PLAN_TIERS[idx - 1] : null;
  }, [recommended]);

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-12 sm:px-6 lg:px-8 font-sans">
      <div className="flex items-center justify-between mb-8">
        {onSwitchView && (
          <button
            onClick={onSwitchView}
            className="text-sm text-slate-400 hover:text-cyan-400 transition-colors font-medium"
          >
            ← View plans
          </button>
        )}
        <CurrencyToggle currency={currency} onChange={setCurrency} />
      </div>

      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">
            Estimate your usage
          </h2>
          <p className="text-slate-400 text-sm">
            Select a navigation profile to start, then fine-tune.
          </p>
        </div>

        <div className="space-y-5 bg-slate-800 p-5 rounded-xl border border-slate-700">
            <div>
              <label className="block text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2.5">
                Navigation profile
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                {(
                  ['Coastal only', 'Multiple regions', 'Worldwide'] as Region[]
                ).map((r) => (
                  <button
                    key={r}
                    onClick={() => handleRegionChange(r)}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg border transition-colors ${
                      region === r
                        ? 'bg-cyan-400/10 border-cyan-400 text-cyan-400'
                        : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.p
                  key={region}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.2 }}
                  className="mt-2 text-[13px] text-slate-500 leading-relaxed"
                >
                  {regionPresets[region].assumption}
                </motion.p>
              </AnimatePresence>
            </div>

            <div>
              <div className="flex justify-between items-end mb-3">
                <label
                  htmlFor="tiles-slider"
                  className="text-sm font-semibold uppercase tracking-wider text-slate-500"
                >
                  Monthly tile downloads
                </label>
                <span className="text-xl font-bold text-cyan-400 tabular-nums">
                  {tiles.toLocaleString()}
                </span>
              </div>
              <input
                id="tiles-slider"
                type="range"
                min="50"
                max="10000"
                step="50"
                value={tiles}
                onChange={(e) => setTiles(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-900 rounded-lg appearance-none cursor-pointer accent-cyan-400"
              />
              <div className="flex justify-between text-[13px] text-slate-500 mt-1.5">
                <span>50</span>
                <span>10,000+</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
                Zoom level
              </label>
              <ZoomPyramid
                activeClusterIndex={activeClusterIndex}
                onChange={setZoom}
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-[13px] text-slate-500">
                  Higher zoom = more tiles per area. Each level roughly quadruples
                  tile count.
                </p>
                <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-2.5 text-center ml-4 shrink-0">
                  <span className="block text-[13px] font-semibold uppercase tracking-wider text-slate-500 mb-0.5">
                    Est. quarterly
                  </span>
                  <motion.span
                    key={quarterlyTokens}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="block text-lg font-bold text-cyan-400 tabular-nums leading-tight"
                  >
                    ~{formatTokens(quarterlyTokens)}
                  </motion.span>
                  <span className="block text-[13px] text-slate-500">tokens</span>
                </div>
              </div>
            </div>

            {/* Devices */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-900 border border-slate-700">
              <div className="flex items-center gap-2.5">
                <DeviceTabletIcon className="w-5 h-5 text-slate-400 rotate-90" />
                <div>
                  <span className="text-sm font-medium text-white">Devices</span>
                  <span className="block text-[13px] text-slate-500">Multi-device pricing coming soon</span>
                </div>
              </div>
              <span className="text-lg font-bold text-white tabular-nums">1</span>
            </div>

            {/* Token estimate breakdown */}
            <div className="rounded-lg bg-slate-900/60 border border-slate-700 px-3 py-2">
              <p className="text-[13px] text-slate-500">
                <span className="text-slate-400 font-medium">~{formatTokens(quarterlyTokens)} tokens/quarter</span>
                {' = '}
                {tiles.toLocaleString()} tiles/mo × {cluster.tileMultiplier}x ({clusterLabel} zoom)
                {' × 3 months'}
              </p>
            </div>

            {/* Recommendation — inline */}
            <div className="h-px w-full bg-slate-700" />

            <motion.div
              key={recommended.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-1">
                    Recommended plan
                  </h3>
                  <h4
                    className={`text-xl font-bold uppercase tracking-wider ${
                      recommended.key === 'bluewater' ? 'text-cyan-400' : 'text-white'
                    }`}
                  >
                    {recommended.name}
                  </h4>
                </div>
                <div className="text-left sm:text-right">
                  <div className="flex items-baseline text-white">
                    <span className="text-3xl font-bold tracking-tight">
                      {formatPrice(recommended.quarterlyPriceNZD, currency)}
                    </span>
                    <span className="ml-1 text-sm font-medium text-slate-400">
                      /quarter
                    </span>
                  </div>
                  <div className="mt-1 inline-flex items-center rounded-md bg-slate-900/50 px-2.5 py-1 text-sm font-medium text-slate-300 border border-slate-700">
                    {formatTokens(recommended.tokensPerQuarter)} tokens/quarter
                  </div>
                </div>
              </div>

              {/* Usage fit bar */}
              <div>
                <div className="flex justify-between text-[13px] mb-1.5">
                  <span className="text-slate-400">Estimated usage</span>
                  <span className="text-slate-300 font-medium tabular-nums">
                    {planFit.pct}% of quota
                  </span>
                </div>
                <div className="h-2 bg-slate-900 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full rounded-full ${
                      planFit.pct > 90 ? 'bg-amber-400' : 'bg-cyan-400'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(planFit.pct, 100)}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
                <p className="text-[13px] text-slate-500 mt-1.5">
                  {planFit.headroom > 0
                    ? `~${formatTokens(planFit.headroom)} tokens headroom per quarter`
                    : 'Usage may exceed quota — overage charges apply'}
                </p>
              </div>

              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckIcon className="mr-2.5 h-5 w-5 shrink-0 text-cyan-400" />
                  <span className="text-sm text-white">
                    Up to zoom level {recommended.maxZoom}
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="mr-2.5 h-5 w-5 shrink-0 text-cyan-400" />
                  <span className="text-sm text-white">
                    {formatTokens(recommended.tokensPerQuarter)} tokens covers
                    your ~{formatTokens(quarterlyTokens)} estimated usage
                  </span>
                </li>
              </ul>

              {nextPlanDown && quarterlyTokens > nextPlanDown.tokensPerQuarter && (
                <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-3">
                  <p className="text-[13px] text-slate-400">
                    <span className="text-slate-300 font-medium">{nextPlanDown.name}</span>
                    {' '}only includes {formatTokens(nextPlanDown.tokensPerQuarter)} tokens/quarter
                    — your estimated {formatTokens(quarterlyTokens)} would exceed that quota.
                  </p>
                </div>
              )}

              {nextPlanDown && quarterlyTokens <= nextPlanDown.tokensPerQuarter && zoom > nextPlanDown.maxZoom && (
                <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-3">
                  <p className="text-[13px] text-slate-400">
                    <span className="text-slate-300 font-medium">{nextPlanDown.name}</span>
                    {' '}only supports up to zoom {nextPlanDown.maxZoom}
                    — your {clusterLabel} zoom ({zoom}) requires {recommended.name}.
                  </p>
                </div>
              )}

              <button
                onClick={() => onSelect(recommended.key)}
                className={`w-full rounded-lg px-4 py-3 text-sm font-semibold transition-all duration-200 ${
                  recommended.key === 'bluewater'
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg shadow-blue-500/25'
                    : 'border border-slate-700 bg-transparent text-white hover:bg-slate-700 hover:border-slate-500'
                }`}
              >
                {recommended.key === 'expedition'
                  ? 'Contact Sales'
                  : `Select ${recommended.name}`}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
  );
}
